import { useRef, useState } from "react";
import { supabase } from "@project/lib/data-client";
import { Button } from "@project/components/ui/button";
import { Upload, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

export function PhotoUpload({
  value,
  onChange,
  folder = "residents",
  size = "md",
}: {
  value?: string | null;
  onChange: (url: string | null) => void;
  folder?: string;
  size?: "sm" | "md";
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const dim = size === "sm" ? "h-16 w-16" : "h-24 w-24";

  const upload = async (file: File) => {
    if (file.size > 5 * 1024 * 1024) {
      return toast.error("Max 5MB");
    }
    setBusy(true);
    const ext = file.name.split(".").pop() ?? "jpg";
    const path = `${folder}/${crypto.randomUUID()}.${ext}`;
    const { error } = await supabase.storage
      .from("resident-photos")
      .upload(path, file, { cacheControl: "3600", upsert: false });
    if (error) {
      setBusy(false);
      return toast.error(error.message);
    }
    const { data } = supabase.storage
      .from("resident-photos")
      .getPublicUrl(path);
    onChange(data.publicUrl);
    setBusy(false);
  };

  return (
    <div className="flex items-center gap-3">
      <div
        className={`${dim} bg-muted/40 grid shrink-0 place-items-center overflow-hidden rounded-xl border-2 border-dashed border-border`}
      >
        {value ? (
          <img src={value} alt="" className="h-full w-full object-cover" />
        ) : (
          <Upload className="h-5 w-5 text-muted-foreground" />
        )}
      </div>
      <div className="flex flex-col gap-1">
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={e => {
            const f = e.target.files?.[0];
            if (f) {
              void upload(f);
            }
            e.currentTarget.value = "";
          }}
        />
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
        >
          {busy ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <Upload className="h-3 w-3" />
          )}{" "}
          {value ? "Change" : "Upload"}
        </Button>
        {value && (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="text-destructive"
            onClick={() => onChange(null)}
          >
            <X className="h-3 w-3" /> Remove
          </Button>
        )}
      </div>
    </div>
  );
}
